import React, { FC } from "react";
import { connect } from "react-redux";

import { faTriangleExclamation } from "@fortawesome/pro-duotone-svg-icons/faTriangleExclamation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";

import localSettings from "@app/config/LocalSettings";
import CardType from "@app/enum/dashboard/CardType";
import GoalType from "@app/enum/dashboard/GoalType";
import getGoalAmount from "@app/scene/dashboard/utils/getGoalAmount";
import useCard from "@app/scene/dashboard/utils/useCard";
import { getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { GoalConfiguration } from "@app/types/dashboard/CardConfiguration";
import Scope from "@app/types/dashboard/Scope";

import styles from "./GoalInformation.module.scss";

export interface GoalInformationProps {
  className?: string;
  currency?: boolean;
  goalConfiguration?: GoalConfiguration;
  scope: Scope;
  type: CardType;
  value?: number;
}

interface Props extends GoalInformationProps {
  teams: Team[];
}

const GoalInformation: FC<Props> = ({
  className,
  currency,
  goalConfiguration,
  scope,
  teams,
  type,
  value = 0,
}) => {
  const intl = useIntl();
  const { getGoalType } = useCard(type);
  const { goalType } = getGoalType({ configuration: goalConfiguration });

  // should not happen, but let's double-check
  if (goalType === GoalType.NO_GOAL) {
    return null;
  }

  const goal = getGoalAmount(goalConfiguration, scope, teams) ?? 0;
  const goalMet = goalType === GoalType.MAX_GOAL ? value <= goal : value >= goal;

  return (
    <div className={cn(styles.container, { [styles.warning]: !goalMet }, className)}>
      {!goalMet && <FontAwesomeIcon icon={faTriangleExclamation} />}{" "}
      {intl.formatMessage(
        {
          id: "dashboard.cards.goalInformation",
          defaultMessage: `{goalType, select, 1 {Min: {goal}} 2 {Max: {goal}} other {}}`,
          description:
            "Text in goal information showing the goal value with the goal type (1 - Min, 2 - Max)",
        },
        {
          goal: intl.formatNumber(
            value,
            currency
              ? {
                  currency: localSettings.getCurrencyCode(),
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                  notation: value >= 10000 ? "compact" : "standard",
                  style: "currency",
                }
              : {
                  maximumFractionDigits: 2,
                  notation: value >= 10000 ? "compact" : "standard",
                }
          ),
          goalType,
        }
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  teams: getTeams(state),
});

export default connect(mapStateToProps)(GoalInformation);
